import { useState } from "react";

import { MAX_GROUP_COUNT, MIN_GROUP_COUNT } from "@canvaschool/contracts";

import { GuideCard } from "../../guide/GuideCard";
import { SETTINGS_ITEMS } from "../../guide/guideSteps";

import type { ClassRecord } from "@canvaschool/contracts";

const chimeMutedKey = (classId: string) => `canvaschool:chimeMuted:${classId}`;

export const SettingsPanel = ({
  classRecord,
  guideUserId,
  onRename,
  onArchive,
  onReactivate,
  onSaveGroupCount,
}: {
  classRecord: ClassRecord;
  /** Teachers see the how-to card; omitted for anyone else. */
  guideUserId?: string;
  onRename: () => void;
  onArchive: () => void;
  onReactivate: () => void;
  onSaveGroupCount: (groupCount: number) => void;
}) => {
  const active = classRecord.status === "ACTIVE";
  const [groupCountInput, setGroupCountInput] = useState(
    String(classRecord.groupCount),
  );
  const [groupCountError, setGroupCountError] = useState<string | null>(null);
  const [chimeMuted, setChimeMuted] = useState(
    () => window.localStorage.getItem(chimeMutedKey(classRecord.id)) === "true",
  );

  const toggleChimeMuted = () => {
    const next = !chimeMuted;
    setChimeMuted(next);
    window.localStorage.setItem(chimeMutedKey(classRecord.id), String(next));
  };

  const saveGroupCount = () => {
    const parsed = Number(groupCountInput);
    if (
      !Number.isInteger(parsed) ||
      parsed < MIN_GROUP_COUNT ||
      parsed > MAX_GROUP_COUNT
    ) {
      setGroupCountError(
        `모둠 수는 ${MIN_GROUP_COUNT}에서 ${MAX_GROUP_COUNT} 사이여야 합니다.`,
      );
      return;
    }
    setGroupCountError(null);
    onSaveGroupCount(parsed);
  };

  return (
    <section aria-labelledby="canvaschool-settings-heading">
      <h2 id="canvaschool-settings-heading">학급 설정</h2>
      {guideUserId && (
        <GuideCard
          id="settings"
          title="학급 설정"
          userId={guideUserId}
          details={SETTINGS_ITEMS}
        >
          학기가 끝나면 학급을 보관하세요.
        </GuideCard>
      )}
      <div className="canvaschool-settings-card">
        <div>
          <h3>학급 이름</h3>
          <div className="canvaschool-actions">
            <strong className="canvaschool-settings-value">
              {classRecord.name}
            </strong>
            {active && (
              <button
                type="button"
                className="canvaschool-btn-primary"
                onClick={onRename}
              >
                학급 이름 변경
              </button>
            )}
          </div>
        </div>
        <div>
          <h3>계정 접두어</h3>
          <span className="canvaschool-prefix">
            {classRecord.accountPrefix}
          </span>
        </div>
        <div>
          <h3>
            <label htmlFor="canvaschool-group-count">모둠 수</label>
          </h3>
          <p>완료 표시를 모둠별로 볼 때 이 개수만큼 칸이 나옵니다.</p>
          <div className="canvaschool-actions">
            <input
              id="canvaschool-group-count"
              type="number"
              min={MIN_GROUP_COUNT}
              max={MAX_GROUP_COUNT}
              value={groupCountInput}
              disabled={!active}
              onChange={(event) => {
                setGroupCountInput(event.target.value);
                setGroupCountError(null);
              }}
            />
            {active && (
              <button
                type="button"
                className="canvaschool-btn-primary"
                onClick={saveGroupCount}
              >
                모둠 수 저장
              </button>
            )}
          </div>
          {groupCountError && (
            <p className="canvaschool-error" role="alert">
              {groupCountError}
            </p>
          )}
        </div>
        <div>
          <h3>완료 알림음</h3>
          <p>
            학생·모둠이 모두 완료로 표시하면 교사 기기에서 알림음이 울립니다.
            여기서 끄면 이 학급 캔버스를 열 때 소리가 나지 않습니다.
          </p>
          <div className="canvaschool-actions">
            <span
              className={`canvaschool-status canvaschool-settings-value ${
                chimeMuted
                  ? "canvaschool-status--off"
                  : "canvaschool-status--on"
              }`}
            >
              {chimeMuted ? "알림음 꺼짐" : "알림음 켜짐"}
            </span>
            <button
              type="button"
              className="canvaschool-btn-primary"
              onClick={toggleChimeMuted}
            >
              {chimeMuted ? "알림음 켜기" : "알림음 끄기"}
            </button>
          </div>
        </div>
        <div className="canvaschool-danger-zone">
          <p>
            학급을 보관하면 학생 로그인과 편집이 중지됩니다. 교사는 읽기와
            내보내기를 계속할 수 있고, 다시 사용할 수도 있습니다.
          </p>
          {active ? (
            <button
              type="button"
              className="canvaschool-btn-danger"
              onClick={onArchive}
            >
              학급 보관
            </button>
          ) : (
            <button
              type="button"
              className="canvaschool-btn-primary"
              onClick={onReactivate}
            >
              학급 다시 사용
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
